import { useEffect, useRef, useState } from "react";
import { FaBug } from "react-icons/fa";
import { useLanguage } from "../i18n/LanguageContext";
import "./css/debuggingOctopus.css";

const reactionKeys = ["planning", "fixed", "tears", "semicolon", "productive"];

export default function DebuggingOctopus() {
  const { t, language } = useLanguage();
  const figure = useRef(null);
  const recoveryTimer = useRef(null);
  const [clicks, setClicks] = useState(0);
  const [crying, setCrying] = useState(false);

  useEffect(() => {
    const mascot = figure.current;
    const eyes = Array.from(mascot.querySelectorAll(".octopus-eye"));
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame;

    function reset() {
      cancelAnimationFrame(frame);
      mascot.style.setProperty("--octopus-tilt", "0deg");
      eyes.forEach((eye) => {
        eye.style.setProperty("--eye-x", "0px");
        eye.style.setProperty("--eye-y", "0px");
      });
    }
    function followPointer(event) {
      if (reducedMotion.matches || event.pointerType === "touch") return;
      cancelAnimationFrame(frame);
      // Move only CSS properties; pointer movement should not rerender the page.
      frame = requestAnimationFrame(() => {
        eyes.forEach((eye) => {
          const rect = eye.getBoundingClientRect();
          const dx = event.clientX - rect.left - rect.width / 2;
          const dy = event.clientY - rect.top - rect.height / 2;
          const distance = Math.hypot(dx, dy) || 1;
          const strength = Math.min(distance / 100, 1);
          eye.style.setProperty(
            "--eye-x",
            `${(dx / distance) * 7 * strength}px`,
          );
          eye.style.setProperty(
            "--eye-y",
            `${(dy / distance) * 9 * strength}px`,
          );
        });
        const rect = mascot.getBoundingClientRect();
        const tilt = Math.max(
          -6,
          Math.min(6, (event.clientX - rect.left - rect.width / 2) / 30),
        );
        mascot.style.setProperty("--octopus-tilt", `${tilt}deg`);
      });
    }
    window.addEventListener("pointermove", followPointer, { passive: true });
    document.documentElement.addEventListener("pointerleave", reset);
    window.addEventListener("blur", reset);
    reducedMotion.addEventListener("change", reset);
    return () => {
      reset();
      window.removeEventListener("pointermove", followPointer);
      document.documentElement.removeEventListener("pointerleave", reset);
      window.removeEventListener("blur", reset);
      reducedMotion.removeEventListener("change", reset);
      clearTimeout(recoveryTimer.current);
    };
  }, []);

  function debug() {
    setClicks((previous) => previous + 1);
    setCrying(true);
    // Restart recovery on every click, including rapid clicks during a reaction.
    clearTimeout(recoveryTimer.current);
    recoveryTimer.current = setTimeout(() => setCrying(false), 3500);
  }

  const line =
    clicks === 0 ? "idle" : reactionKeys[(clicks - 1) % reactionKeys.length];
  return (
    <div className="debugging-octopus">
      <p
        className="octopus-speech"
        lang={language}
        dir={language === "ar" ? "rtl" : "ltr"}
        aria-live="polite"
        aria-atomic="true"
      >
        {t(`home.octopus.${line}`)}
      </p>
      <div className="octopus-stage">
        <div className="octopus-halo" aria-hidden="true" />
        <div className="octopus-shadow" aria-hidden="true" />
        <button
          ref={figure}
          type="button"
          className={`octopus-mascot${crying ? " is-crying" : ""}`}
          onClick={debug}
          aria-label={t("home.octopus.action")}
          aria-describedby="octopus-hint"
        >
          <span className="octopus-art" aria-hidden="true">
            {Array.from({ length: 8 }, (_, index) => (
              <span
                className="octopus-arm"
                style={{ "--arm-index": index }}
                key={index}
              />
            ))}
            <span className="octopus-head" />
            <span className="octopus-cheek left" />
            <span className="octopus-cheek right" />
            <span className="octopus-eyes">
              <span className="octopus-eye">
                <span className="octopus-pupil" />
              </span>
              <span className="octopus-eye">
                <span className="octopus-pupil" />
              </span>
            </span>
            <span className="octopus-mouth" />
            {crying && (
              <span key={clicks} className="octopus-tears">
                <span className="octopus-tear left" />
                <span className="octopus-tear right" />
              </span>
            )}
            <span className="octopus-bug">
              <FaBug />
            </span>
            <span className="octopus-laptop">
              <span>&lt;/&gt;</span>
              <span className="octopus-screen-status">
                {crying ? "bug → 3 bugs" : "debugging..."}
              </span>
            </span>
          </span>
        </button>
      </div>
      <p className="octopus-hint" id="octopus-hint">
        {t("home.octopus.hint")}
      </p>
    </div>
  );
}
